import { useCallback, useEffect, useMemo, useState } from 'react';
import { collection, onSnapshot, orderBy, query } from 'firebase/firestore';
import { getFirebaseDb } from '../services/firebase';
import {
  addDayFocusItem,
  createDayFocus,
  deleteDayFocus,
  deleteDayFocusItem,
  getTodayDateKey,
  renameDayFocusItem,
  reorderDayFocusItems,
  setDayFocusItemParent,
  toggleDayFocusItem,
  updateDayFocusDate,
  updateDayFocusDueDate,
  updateDayFocusProject,
  updateDayFocusSwimLane,
  updateDayFocusTitle,
} from '../services/dayFocus';
import type { ActionItemStatus, DayFocus, DayFocusPriority, TodoSwimLane } from '../types';

interface CreateDayFocusInput {
  title: string;
  summary?: string;
  priority: DayFocusPriority;
  actionItems: string[];
  focusDate?: string;
  sessionId?: string;
}

interface UseDayFocusReturn {
  todayFocuses: DayFocus[];
  tomorrowFocuses: DayFocus[];
  allFocuses: DayFocus[];
  primaryFocus: DayFocus | null;
  secondaryFocus: DayFocus | null;
  loading: boolean;
  createFocus: (input: CreateDayFocusInput) => Promise<string | null>;
  toggle: (focusId: string, actionItemId: string, completed: boolean) => Promise<void>;
  addItem: (
    focusId: string,
    text: string,
    options?: { parentItemId?: string | null; afterItemId?: string },
  ) => Promise<string | null>;
  deleteItem: (focusId: string, actionItemId: string) => Promise<void>;
  renameItem: (focusId: string, actionItemId: string, text: string) => Promise<void>;
  reorder: (
    focusId: string,
    orderedItemIds: string[],
    statusByItemId?: Partial<Record<string, ActionItemStatus>>,
    parentByItemId?: Partial<Record<string, string | null>>,
  ) => Promise<void>;
  setItemParent: (focusId: string, actionItemId: string, parentItemId: string | null) => Promise<void>;
  renameFocus: (focusId: string, title: string) => Promise<void>;
  deleteFocus: (focusId: string) => Promise<void>;
  moveFocusToDate: (focusId: string, date: string) => Promise<void>;
  setSwimLane: (focusId: string, lane: TodoSwimLane | null) => Promise<void>;
  setProject: (focusId: string, projectId: string | null) => Promise<void>;
  setDueDate: (focusId: string, dueDate: string | null) => Promise<void>;
}

export function useDayFocus(userId: string | null): UseDayFocusReturn {
  const [allFocuses, setAllFocuses] = useState<DayFocus[]>([]);
  const [loading, setLoading] = useState(true);
  const todayKey = useMemo(() => getTodayDateKey(), []);
  const tomorrowKey = useMemo(() => {
    const d = new Date();
    d.setDate(d.getDate() + 1);
    return getTodayDateKey(d);
  }, []);

  useEffect(() => {
    if (!userId) {
      setAllFocuses([]);
      setLoading(false);
      return;
    }

    const db = getFirebaseDb();
    const q = query(
      collection(db, 'users', userId, 'dayFocus'),
      orderBy('createdAt', 'desc')
    );

    const unsub = onSnapshot(
      q,
      (snap) => {
        setAllFocuses(snap.docs.map((d) => ({ id: d.id, ...d.data() } as DayFocus)));
        setLoading(false);
      },
      () => setLoading(false)
    );

    return unsub;
  }, [userId]);

  const todayFocuses = useMemo(
    () => allFocuses.filter((focus) => focus.focusDate === todayKey),
    [allFocuses, todayKey]
  );

  const tomorrowFocuses = useMemo(
    () => allFocuses.filter((focus) => focus.focusDate === tomorrowKey),
    [allFocuses, tomorrowKey]
  );

  const primaryFocus = useMemo(
    () => todayFocuses.find((focus) => focus.priority === 'primary') ?? null,
    [todayFocuses]
  );

  const secondaryFocus = useMemo(
    () => todayFocuses.find((focus) => focus.priority === 'secondary') ?? null,
    [todayFocuses]
  );

  const createFocus = useCallback(
    async (input: CreateDayFocusInput) => {
      if (!userId) return null;

      const items = input.actionItems
        .map((text) => text.trim())
        .filter(Boolean)
        .map((text, order) => ({
          id: `action-${Date.now()}-${order}`,
          text,
          order,
        }));

      if (!input.title.trim() || items.length === 0) return null;

      return createDayFocus(
        userId,
        input.sessionId ?? 'manual',
        input.title.trim(),
        input.summary?.trim() || undefined,
        input.priority,
        items,
        input.focusDate ?? todayKey
      );
    },
    [userId, todayKey]
  );

  const toggle = useCallback(
    async (focusId: string, actionItemId: string, completed: boolean) => {
      if (!userId) return;
      await toggleDayFocusItem(userId, focusId, actionItemId, completed);
    },
    [userId]
  );

  const addItem = useCallback(
    async (
      focusId: string,
      text: string,
      options?: { parentItemId?: string | null; afterItemId?: string },
    ) => {
      if (!userId) return null;
      return addDayFocusItem(userId, focusId, text, options);
    },
    [userId]
  );

  const deleteItem = useCallback(
    async (focusId: string, actionItemId: string) => {
      if (!userId) return;
      await deleteDayFocusItem(userId, focusId, actionItemId);
    },
    [userId]
  );

  const renameItem = useCallback(
    async (focusId: string, actionItemId: string, text: string) => {
      if (!userId) return;
      await renameDayFocusItem(userId, focusId, actionItemId, text);
    },
    [userId]
  );

  const reorder = useCallback(
    async (
      focusId: string,
      orderedItemIds: string[],
      statusByItemId?: Partial<Record<string, ActionItemStatus>>,
      parentByItemId?: Partial<Record<string, string | null>>,
    ) => {
      if (!userId) return;
      await reorderDayFocusItems(userId, focusId, orderedItemIds, statusByItemId, parentByItemId);
    },
    [userId]
  );

  const setItemParent = useCallback(
    async (focusId: string, actionItemId: string, parentItemId: string | null) => {
      if (!userId) return;
      await setDayFocusItemParent(userId, focusId, actionItemId, parentItemId);
    },
    [userId]
  );

  const renameFocus = useCallback(
    async (focusId: string, title: string) => {
      if (!userId) return;
      await updateDayFocusTitle(userId, focusId, title);
    },
    [userId]
  );

  const deleteFocusCb = useCallback(
    async (focusId: string) => {
      if (!userId) return;
      await deleteDayFocus(userId, focusId);
    },
    [userId]
  );

  const moveFocusToDate = useCallback(
    async (focusId: string, date: string) => {
      if (!userId) return;
      await updateDayFocusDate(userId, focusId, date);
    },
    [userId]
  );

  const setSwimLane = useCallback(
    async (focusId: string, lane: TodoSwimLane | null) => {
      if (!userId) return;
      await updateDayFocusSwimLane(userId, focusId, lane);
    },
    [userId]
  );

  const setProject = useCallback(
    async (focusId: string, projectId: string | null) => {
      if (!userId) return;
      await updateDayFocusProject(userId, focusId, projectId);
    },
    [userId]
  );

  const setDueDate = useCallback(
    async (focusId: string, dueDate: string | null) => {
      if (!userId) return;
      await updateDayFocusDueDate(userId, focusId, dueDate);
    },
    [userId]
  );

  return {
    todayFocuses,
    tomorrowFocuses,
    allFocuses,
    primaryFocus,
    secondaryFocus,
    loading,
    createFocus,
    toggle,
    addItem,
    deleteItem,
    renameItem,
    reorder,
    setItemParent,
    renameFocus,
    deleteFocus: deleteFocusCb,
    moveFocusToDate,
    setSwimLane,
    setProject,
    setDueDate,
  };
}
